import {
    Alert,
    Container,
    Divider,
    getPageValidationInfo,
    Header,
    Label,
    SummaryGroup,
    useAlignments,
    useSizes,
} from 'metrix-common3';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import ProfileSecureMyOfferSeller from './Contact';

const MAX_ADDITIONAL_SELLERS = 3;
const CONTACT_TYPE_SELLER = 1;

const ProfileSecureMyOfferAdditionalSellers = (props) => {
    const alignments = useAlignments();
    const sizes = useSizes();

    const [mode, setMode] = useState('list');
    const [currentSellerGuid, setCurrentSellerGuid] = useState(null);
    const [isNewSeller, setIsNewSeller] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only init
    useEffect(() => {
        const pageHasTooltipInfo =
            props.tooltipInfo?.pages?.length && props.tooltipInfo.pages.some((p) => p.code === props.pageCode);
        if (!pageHasTooltipInfo) {
            props.doGetTooltipInfo(props.policy.product);
        }
    }, []);

    const sellers = props.policy?.additionalContacts ? props.policy.additionalContacts : [];

    const getEntities = () => {
        return sellers.map((seller) => {
            const label = `${seller.firstName || ''} ${seller.lastName || ''}`.trim() || 'New';
            return {
                code: 'additionalSellerOption',
                value: seller.guid,
                elements: [{ code: 'Label', value: label }],
            };
        });
    };

    const handleAdd = () => {
        const newSeller = {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            contactType: CONTACT_TYPE_SELLER,
        };

        props.doReducePolicyElement({ code: 'additionalContacts', value: [...sellers, newSeller] });
        setIsNewSeller(true);
        setCurrentSellerGuid(null);
        setMode('detail');
    };

    const handleEdit = (guid) => {
        setCurrentSellerGuid(guid);
        setIsNewSeller(false);
        setMode('detail');
    };

    const handleDelete = (guid) => {
        props.doReduceParameter({ code: 'AdditionalContactGuid', value: guid });
        props.doExecutePolicyAction({ code: 'SECUREMYOFFER.DELETEADDITIONALCONTACT' });
    };

    const handleBack = () => {
        if (isNewSeller) {
            props.doReducePolicyElement({ code: 'additionalContacts', value: sellers.filter((s) => s.guid) });
        }
        setMode('list');
        setCurrentSellerGuid(null);
        setIsNewSeller(false);
    };

    const handleSave = () => {
        props.doUpsertPolicy();
        setMode('list');
        setCurrentSellerGuid(null);
        setIsNewSeller(false);
    };

    const handleCancel = () => {
        props.doGetPolicy();
        setMode('list');
        setCurrentSellerGuid(null);
        setIsNewSeller(false);
    };

    const { error, warning } = getPageValidationInfo(props.pageCode, props.validationInfo);

    if (mode === 'detail') {
        let sellerIndex;
        let seller;

        if (isNewSeller) {
            sellerIndex = sellers.length - 1;
            seller = sellers[sellerIndex];
        } else {
            sellerIndex = sellers.findIndex((s) => s.guid === currentSellerGuid);
            seller = sellerIndex >= 0 ? sellers[sellerIndex] : null;
        }

        return (
            <ProfileSecureMyOfferSeller
                rowId={currentSellerGuid}
                pageCode={props.pageCode}
                saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                tooltipInfo={props.tooltipInfo}
                validationInfo={props.validationInfo}
                seller={seller}
                dotProp={`additionalContacts.${sellerIndex}.`}
                backEvent={handleBack}
                saveEvent={handleSave}
                cancelEvent={handleCancel}
                doReducePolicyElement={props.doReducePolicyElement}
                reference={props.reference}
                context={props.context}
                allowEdit={props.allowEdit}
            />
        );
    }

    const entities = getEntities();

    return (
        <React.Fragment>
            {(error.message.length !== 0 || warning.message.length !== 0) && (
                <Container top={sizes.md}>
                    {error.message.length !== 0 && <Alert mode='danger' header={error.title} items={error.message} />}
                    {warning.message.length !== 0 && (
                        <Alert mode='warning' header={warning.title} items={warning.message} />
                    )}
                </Container>
            )}
            <Divider top={sizes.md} />
            <Header
                size={sizes.md}
                textAlign={alignments.left}
                text={<FormattedMessage id='Profile_SecureMyOffer_AdditionalSellers.additionalSellers' />}
            />
            <Label
                top={sizes.xs}
                textAlign={alignments.left}
                text={<FormattedMessage id='Profile_SecureMyOffer_AdditionalSellers.description' />}
            />
            <Container top={sizes.md} bottom={sizes.xl}>
                <SummaryGroup
                    allowAdd={props.allowEdit && sellers.length < MAX_ADDITIONAL_SELLERS}
                    addEvent={handleAdd}
                    allowEdit
                    editEvent={handleEdit}
                    allowDelete={props.allowEdit && sellers.length > 0}
                    deleteEvent={handleDelete}
                    entities={entities}
                    icon='person'
                />
            </Container>
        </React.Fragment>
    );
};

export default ProfileSecureMyOfferAdditionalSellers;
